import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CheckCircle, Lock, Trash2 } from 'lucide-react';
import { useState } from 'react';

export const DataDeletionPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = 'Email required';
    if (!password) newErrors.password = 'Password required';
    if (!confirmation) newErrors.confirmation = 'Confirmation required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) setIsSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-16 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto"
      >
        <div className="text-center mb-16 space-y-3">
          <motion.div 
            className="inline-flex bg-red-100/80 p-5 rounded-2xl backdrop-blur-sm"
            whileHover={{ scale: 1.05 }}
          >
            <Lock className="w-10 h-10 text-red-600" />
          </motion.div>
          <h1 className="text-4xl font-bold text-navy-900 bg-clip-text">
            Account Data Removal
          </h1>
          <p className="text-gray-600 text-lg mt-2">
            Secure permanent deletion of your digital footprint
          </p>
        </div>

        <AnimatePresence mode='wait'>
          {!isSubmitted ? (
            <motion.form
              onSubmit={handleSubmit}
              className="bg-white shadow-xl rounded-2xl p-8 lg:p-12"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="mb-10 bg-red-50/50 p-6 rounded-xl border border-red-200 backdrop-blur-sm">
                <div className="flex gap-4 items-start">
                  <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-1" />
                  <div>
                    <h3 className="text-red-900 font-semibold text-lg mb-3">
                      Permanent Account Termination
                    </h3>
                    <ul className="list-disc pl-5 text-red-800 space-y-2.5 text-sm">
                      <li className="leading-relaxed">Immediate loss of platform access</li>
                      <li className="leading-relaxed">Irreversible deletion of all stored data</li>
                      <li className="leading-relaxed">Cancellation of active subscriptions</li>
                      <li className="leading-relaxed">Removal from collaboration spaces</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Account Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-300/80 focus:ring-2 focus:ring-red-500 focus:border-transparent placeholder-gray-400"
                      placeholder="name@company.com"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-300/80 focus:ring-2 focus:ring-red-500 focus:border-transparent placeholder-gray-400"
                      placeholder="••••••••"
                    />
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Reason for Deletion
                    </label>
                    <select
                      value={deletionReason}
                      onChange={(e) => setDeletionReason(e.target.value)}
                      className="w-full px-5 py-3.5 rounded-xl border border-gray-300/80 focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-700"
                    >
                      <option value="">Select primary reason...</option>
                      <option>Privacy Concerns</option>
                      <option>Service Dissatisfaction</option>
                      <option>Data Management</option>
                      <option>Account Security</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="relative flex items-start space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-200/50">
                  <div className="flex items-center h-5 mt-0.5">
                    <input
                      type="checkbox"
                      checked={confirmation}
                      onChange={(e) => setConfirmation(e.target.checked)}
                      className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-gray-700 text-sm leading-relaxed">
                      I acknowledge and understand that this action will permanently erase all my personal data, 
                      account information, and associated content from TemplateHub's systems. This process is 
                      irreversible and will result in immediate loss of access to all platform services.
                    </label>
                    {errors.confirmation && (
                      <p className="text-red-500 text-sm mt-2 flex items-center gap-1.5">
                        <AlertTriangle className="w-4 h-4" />
                        {errors.confirmation}
                      </p>
                    )}
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-red-600 text-white py-4 px-8 rounded-xl font-medium hover:bg-red-700 transition-all flex items-center justify-center gap-3 shadow-lg shadow-red-100"
                  disabled={!confirmation}
                >
                  <Trash2 className="w-5 h-5" />
                  Request Termination
                </motion.button>
              </div>
            </motion.form>
          ) : (
            <motion.div
              className="bg-white shadow-xl rounded-2xl p-10 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="mb-8 inline-flex bg-green-100/80 p-6 rounded-2xl backdrop-blur-sm">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Deletion Request Confirmed
              </h2>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed max-w-2xl mx-auto">
                We've initiated the secure deletion protocol for <span className="font-semibold text-gray-700">{email}</span>. 
                You'll receive final confirmation and audit trail within 24 hours.
              </p>
              
              <div className="bg-blue-50/50 p-8 rounded-xl border border-blue-200 text-left space-y-4 backdrop-blur-sm">
                <h3 className="text-blue-900 font-medium text-lg mb-4">
                  Deletion Process Timeline
                </h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-blue-600 font-medium">1</span>
                    </div>
                    <div>
                      <p className="text-blue-900 font-medium">Immediate Deactivation</p>
                      <p className="text-blue-800 text-sm mt-1">All account access revoked within 5 minutes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-blue-600 font-medium">2</span>
                    </div>
                    <div>
                      <p className="text-blue-900 font-medium">Data Purge Initiated</p>
                      <p className="text-blue-800 text-sm mt-1">72-hour secure deletion process from all systems</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-blue-600 font-medium">3</span>
                    </div>
                    <div>
                      <p className="text-blue-900 font-medium">Final Confirmation</p>
                      <p className="text-blue-800 text-sm mt-1">Audit report and completion certificate sent via email</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-16 text-center text-sm text-gray-500 space-y-2">
          <p>
            Require assistance? Contact our{" "}
            <a href="/support" className="text-red-600 hover:text-red-800 font-medium">
              Data Protection Team
            </a>
          </p>
          <p className="text-xs text-gray-400 mt-4">
            GDPR Compliance ID: DPR-2024-00983 • Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </motion.div>
    </div>
  );
};